import React, { useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { apiRequest } from '../api';
import { useApp } from '../App';

function CopyButton({ text }) {
  const [copied, setCopied] = React.useState(false);
  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <button className="secondary-action copy-btn" type="button" onClick={handleCopy}>
      {copied ? 'Copied!' : 'Copy all codes'}
    </button>
  );
}

export default function SecurityPage() {
  const hasAutoLoadedSetupRef = useRef(false);
  const [apiTokens, setApiTokens] = React.useState([]);
  const [apiTokenBusy, setApiTokenBusy] = React.useState(false);
  const [apiTokenName, setApiTokenName] = React.useState('CLI access');
  const [apiTokenDays, setApiTokenDays] = React.useState('30');
  const [newApiToken, setNewApiToken] = React.useState('');
  const [apiTokenError, setApiTokenError] = React.useState('');
  const [tokenCopied, setTokenCopied] = React.useState(false);
  const {
    authToken,
    securityPosture,
    mfaSetup, mfaEnableCode, setMfaEnableCode,
    mfaDisableForm, setMfaDisableForm,
    recoveryCodes,
    auditEvents, auditError,
    authBusy, authMessage, authError,
    handleMfaSetup, handleMfaEnable, handleMfaDisable, handleRecoveryCodesRegenerate,
    resetMessages,
    currentUser,
  } = useApp();

  async function handleCopyApiToken(value) {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    } catch {
      setTokenCopied(false);
    }
  }

  async function loadApiTokens() {
    if (!authToken) return;
    setApiTokenBusy(true);
    setApiTokenError('');
    try {
      const data = await apiRequest('/api/v1/auth/api-tokens', {}, authToken);
      setApiTokens(data || []);
    } catch (error) {
      setApiTokenError(error.message || 'Failed to load API tokens.');
    } finally {
      setApiTokenBusy(false);
    }
  }

  async function handleCreateApiToken(event) {
    event.preventDefault();
    if (!authToken) return;
    setApiTokenBusy(true);
    setApiTokenError('');
    try {
      const created = await apiRequest('/api/v1/auth/api-tokens', {
        method: 'POST',
        body: JSON.stringify({
          name: apiTokenName.trim() || 'CLI access',
          expires_in_days: Number(apiTokenDays || '30'),
        }),
      }, authToken);
      setNewApiToken(created.token || '');
      await loadApiTokens();
    } catch (error) {
      setApiTokenError(error.message || 'Failed to create token.');
    } finally {
      setApiTokenBusy(false);
    }
  }

  async function handleRevokeApiToken(tokenId) {
    if (!authToken) return;
    setApiTokenBusy(true);
    setApiTokenError('');
    try {
      await apiRequest(`/api/v1/auth/api-tokens/${tokenId}/revoke`, {
        method: 'POST',
      }, authToken);
      await loadApiTokens();
    } catch (error) {
      setApiTokenError(error.message || 'Failed to revoke token.');
    } finally {
      setApiTokenBusy(false);
    }
  }

  async function handleDeleteApiToken(tokenId) {
    if (!authToken) return;
    setApiTokenBusy(true);
    setApiTokenError('');
    try {
      await apiRequest(`/api/v1/auth/api-tokens/${tokenId}`, {
        method: 'DELETE',
      }, authToken);
      await loadApiTokens();
    } catch (error) {
      setApiTokenError(error.message || 'Failed to delete token.');
    } finally {
      setApiTokenBusy(false);
    }
  }

  // Clear any auth messages left over from login/register when this page mounts
  useEffect(() => {
    resetMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!securityPosture?.mfa_setup_pending || securityPosture?.mfa_enabled) {
      hasAutoLoadedSetupRef.current = false;
      return;
    }

    if (mfaSetup || authBusy || hasAutoLoadedSetupRef.current) {
      return;
    }

    hasAutoLoadedSetupRef.current = true;
    handleMfaSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityPosture?.mfa_setup_pending, securityPosture?.mfa_enabled, mfaSetup, authBusy]);

  useEffect(() => {
    if (!authToken) return;
    loadApiTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  return (
    <div className="page-view">
      <div className="page-header">
        <p className="feature-eyebrow">Security</p>
        <h2>Security posture</h2>
        <p className="page-subtitle">Manage authenticator MFA, recovery codes, and review your audit log.</p>
      </div>

      {currentUser && !securityPosture?.mfa_enabled && (
        <div className="workspace-block mfa-required-banner">
          <strong>MFA setup required.</strong> Scan the QR code below and confirm with a 6-digit authenticator code to secure your account before using Sroik.
        </div>
      )}

      <div className="workspace-grid">
        <div className="workspace-block">
          <h4>Account security</h4>
          <p>Plan: <strong>{securityPosture?.tenant_plan || '—'}</strong></p>
          <p>MFA enabled: <strong>{securityPosture?.mfa_enabled ? 'Yes' : 'No'}</strong></p>
          {!securityPosture?.mfa_enabled && securityPosture?.mfa_setup_pending && (
            <p className="form-hint">MFA setup is pending verification.</p>
          )}
          <p>Password reset: <strong>{securityPosture?.password_reset_enabled ? 'Enabled' : 'Disabled'}</strong></p>
        </div>

        <div className="workspace-block">
          <h4>API access tokens</h4>
          <p className="form-hint">Create, revoke, and delete personal API tokens for CLI and automation.</p>

          <form className="auth-form inline-form" onSubmit={handleCreateApiToken}>
            <label>
              Token name
              <input
                type="text"
                value={apiTokenName}
                onChange={(e) => setApiTokenName(e.target.value)}
                placeholder="CI token"
                required
              />
            </label>
            <label>
              Expires in days
              <input
                type="number"
                min="1"
                max="365"
                value={apiTokenDays}
                onChange={(e) => setApiTokenDays(e.target.value)}
                required
              />
            </label>
            <div className="workspace-actions">
              <button className="primary-action" type="submit" disabled={apiTokenBusy}>
                {apiTokenBusy ? 'Working...' : 'Create token'}
              </button>
            </div>
          </form>

          {newApiToken && (
            <div className="setup-snippet">
              <div className="setup-snippet-header">
                <span>New token (shown once)</span>
                <button className="secondary-action inline-action" type="button" onClick={() => handleCopyApiToken(newApiToken)}>
                  {tokenCopied ? 'Copied!' : 'Copy token'}
                </button>
              </div>
              <pre className="mono-text">{newApiToken}</pre>
              <pre className="mono-text" aria-label="cURL example">
{`curl -H "Authorization: Bearer ${newApiToken}" \\
  -H "Content-Type: application/json" \\
  https://api.sroik.com/api/v1/cloud-accounts`}
              </pre>
            </div>
          )}

          {apiTokenError && <p className="auth-feedback auth-error">{apiTokenError}</p>}

          {!apiTokenError && apiTokens.length === 0 ? (
            <p className="form-hint">No API tokens created yet.</p>
          ) : (
            apiTokens.length > 0 && (
              <ul className="card-list compact-list">
                {apiTokens.map((token) => (
                  <li key={token.id} style={token.revoked_at ? {opacity: 0.6} : {}}>
                    <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                      <strong>{token.name}</strong>
                      {token.revoked_at
                        ? <span style={{fontSize:'0.72rem', fontWeight:600, color:'#b00020', background:'#fdecea', border:'1px solid #f5c6cb', borderRadius:'4px', padding:'1px 7px', letterSpacing:'0.04em'}}>REVOKED</span>
                        : <span style={{fontSize:'0.72rem', fontWeight:600, color:'#1a7f37', background:'#dcfce7', border:'1px solid #bbf7d0', borderRadius:'4px', padding:'1px 7px', letterSpacing:'0.04em'}}>ACTIVE</span>
                      }
                    </div>
                    <span className="account-hint">Prefix: {token.token_prefix}</span>
                    <span className="account-hint">
                      Created: {new Date(token.created_at).toLocaleString()}
                      {token.expires_at ? ` | Expires: ${new Date(token.expires_at).toLocaleString()}` : ' | Expires: Never'}
                    </span>
                    {token.revoked_at && (
                      <span className="account-hint">Revoked: {new Date(token.revoked_at).toLocaleString()}</span>
                    )}
                    {token.last_used_at && (
                      <span className="account-hint">Last used: {new Date(token.last_used_at).toLocaleString()}</span>
                    )}
                    <div className="workspace-actions">
                      {!token.revoked_at && (
                        <button
                          className="secondary-action inline-action"
                          type="button"
                          disabled={apiTokenBusy}
                          onClick={() => handleRevokeApiToken(token.id)}
                        >
                          Revoke
                        </button>
                      )}
                      <button
                        className="secondary-action inline-action"
                        type="button"
                        disabled={apiTokenBusy}
                        onClick={() => handleDeleteApiToken(token.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )
          )}
        </div>

        {!securityPosture?.mfa_enabled && (
          <div className="workspace-block">
            <h4>Authenticator integration</h4>
            {securityPosture?.mfa_setup_pending && (
              <p className="form-hint">Setup is pending. Click below to resume and complete MFA verification.</p>
            )}
            <div className="workspace-actions">
              <button className="primary-action" disabled={authBusy} onClick={handleMfaSetup} type="button">
                {securityPosture?.mfa_setup_pending ? 'Resume setup secret' : 'Generate setup secret'}
              </button>
            </div>
          </div>
        )}
      </div>

      {mfaSetup && !securityPosture?.mfa_enabled && (
        <div className="workspace-block mfa-block">
          <h4>Authenticator setup</h4>
          <p className="form-hint">Add this to your authenticator app, then submit a 6-digit code to finish enabling MFA.</p>
          <div className="mfa-setup-panel">
            <div className="mfa-qr-card" aria-label="Authenticator QR code">
              <QRCodeSVG
                value={mfaSetup.provisioning_uri}
                size={192}
                marginSize={2}
                bgColor="transparent"
                fgColor="#0b1628"
                level="M"
                includeMargin={false}
              />
            </div>
            <div className="mfa-setup-copy">
              <p>Issuer: {mfaSetup.issuer}</p>
              <p>Manual entry key: <span className="mono-text">{mfaSetup.manual_entry_key}</span></p>
              <p>Provisioning URI: <span className="mono-text">{mfaSetup.provisioning_uri}</span></p>
            </div>
          </div>
          <form className="auth-form inline-form" onSubmit={handleMfaEnable}>
            <label>
              6-digit authenticator code
              <input value={mfaEnableCode} onChange={(e) => setMfaEnableCode(e.target.value)} type="text" maxLength="6" required />
            </label>
            <button className="primary-action" disabled={authBusy} type="submit">Enable MFA</button>
          </form>
        </div>
      )}

      {securityPosture?.mfa_enabled && (
        <form className="auth-form inline-form" onSubmit={handleMfaDisable}>
          <h4>Disable authenticator MFA</h4>
          <label>
            Authenticator code
            <input value={mfaDisableForm.authenticator_code} onChange={(e) => setMfaDisableForm({ ...mfaDisableForm, authenticator_code: e.target.value })} type="text" maxLength="6" />
          </label>
          <label>
            Recovery code
            <input value={mfaDisableForm.recovery_code} onChange={(e) => setMfaDisableForm({ ...mfaDisableForm, recovery_code: e.target.value.toUpperCase() })} type="text" />
          </label>
          <button className="secondary-action" disabled={authBusy} type="submit">Disable MFA</button>
        </form>
      )}

      {securityPosture?.mfa_enabled && (
        <form className="auth-form inline-form" onSubmit={handleRecoveryCodesRegenerate}>
          <h4>Regenerate recovery codes</h4>
          <p className="form-hint">Use an authenticator code or an existing recovery code to issue a fresh set.</p>
          <label>
            Authenticator code
            <input value={mfaDisableForm.authenticator_code} onChange={(e) => setMfaDisableForm({ ...mfaDisableForm, authenticator_code: e.target.value })} type="text" maxLength="6" />
          </label>
          <label>
            Recovery code
            <input value={mfaDisableForm.recovery_code} onChange={(e) => setMfaDisableForm({ ...mfaDisableForm, recovery_code: e.target.value.toUpperCase() })} type="text" />
          </label>
          <button className="secondary-action" disabled={authBusy} type="submit">Regenerate recovery codes</button>
        </form>
      )}

      {recoveryCodes.length > 0 && (
        <div className="workspace-block recovery-block">
          <h4>Recovery codes</h4>
          <p className="form-hint">Store these once. Each code can be used a single time.</p>
          <ul className="recovery-grid">
            {recoveryCodes.map((code) => (
              <li className="mono-text" key={code}>{code}</li>
            ))}
          </ul>
          <CopyButton text={recoveryCodes.join('\n')} />
          <pre className="recovery-export mono-text" aria-label="Recovery codes export">{recoveryCodes.join('\n')}</pre>
        </div>
      )}

      {authMessage && <p className="auth-feedback auth-success">{authMessage}</p>}
      {authError && <p className="auth-feedback auth-error">{authError}</p>}

      <div id="audit-log" className="workspace-block">
        <h4>Audit log</h4>
        {auditEvents.length === 0 ? (
          <p>No audit events found for this workspace yet.</p>
        ) : (
          <ul className="card-list compact-list audit-list">
            {auditEvents.map((event) => (
              <li className="audit-item" key={event.id}>
                <div className="audit-item-header">
                  <strong>{String(event.event_type || '').replaceAll('_', ' ')}</strong>
                  <span className={`audit-status audit-status-${(event.status || 'unknown').toLowerCase()}`}>
                    {String(event.status || '').toUpperCase()}
                  </span>
                </div>
                <div className="audit-item-meta">
                  <span>{new Date(event.created_at).toLocaleString()}</span>
                  {event.actor_email && <span title="User">&#128100; {event.actor_email}</span>}
                  {event.source && (
                    <span title="Source" className={`audit-source audit-source-${event.source}`}>
                      {event.source === 'api' ? '&#128279; API' : '&#128396; UI'}
                    </span>
                  )}
                  {event.ip_address && <span title="IP address">&#127758; {event.ip_address}</span>}
                </div>
                {event.user_agent && (
                  <div className="audit-item-ua" title={event.user_agent}>
                    {event.user_agent.length > 80 ? event.user_agent.slice(0, 80) + '…' : event.user_agent}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {auditError && <p className="form-hint">{auditError}</p>}
      </div>
    </div>
  );
}
